/**
 * Predicates - Type checking utilities
 */

import { ClientApplication } from '../api-clients/dcr-api'
import { ErrorInformation } from '../api-clients/rest-api-v2/types.gen'
import { AccessTokenError, AccessTokenErrorValues, ClientRegistrationError, ClientRegistrationErrorValues, DcrError, DcrErrorValues, RestErrorAction, RestErrorActionValues, RestErrorCode, RestErrorCodeValues, RestError, RestErrorStatusValues, RestErrorStatus } from '../ClientlessAuthErrors'

/**
 * Check if the object is the expected shape of a {@link ClientApplication}
 * @param object The data to check
 * @returns 'true` if `object` is of type 'ClientApplication`
 */
export function isClientApplication(object: unknown): object is ClientApplication {
  const clientApp = object as ClientApplication

  return clientApp?.client_id !== undefined
    && clientApp?.client_secret !== undefined
    && clientApp?.grant_types !== undefined
}

/**
 * Check if the object is a REST v2 error response body from Adobe.
 * @param object The error response data to check
 * @returns `true` if `object` is of type {@link ErrorInformation}
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-representation-structure
 */
export function isErrorInformation(object: unknown): object is ErrorInformation {
  return (
    (object as ErrorInformation)?.status !== undefined
    && (object as ErrorInformation)?.code !== undefined
    && (object as ErrorInformation)?.action !== undefined
  )
}

/**
 * Check if the specified `action` is a known REST v2 enhanced error action.
 * @param action The error response data to check
 * @returns `true` if `action` is on the list of {@link RestErrorActionValues}
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-representation-action
 */
export function isRestErrorAction(code: string): code is RestErrorAction {
  return RestErrorActionValues.includes(code as RestErrorAction)
}

/**
 * Check if the specified `code` is a known REST v2 enhanced error code.
 * @param code The error response data to check
 * @returns `true` if `code` is on the list of {@link RestErrorCodeValues}
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-lists-rest-api-v2
 */
export function isRestErrorCode(code: string): code is RestErrorCode {
  return RestErrorCodeValues.includes(code as RestErrorCode)
}

/**
 * Check if the specified `status` is a known REST v2 enhanced error status.
 * @param status The error response data to check
 * @returns `true` if `status` is on the list of {@link RestErrorStatusValues}
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-representation-status
 */
export function isRestErrorStatus(status: number): status is RestErrorStatus {
  return RestErrorStatusValues.includes(status as RestErrorStatus)
}

/**
 * Check if the object is a REST v2 error response body from Adobe.
 * @param object The error response data to check
 * @returns `true` if `object` is of type {@link ErrorInformation}
 * @see https://experienceleague.adobe.com/en/docs/pass/authentication/integration-guide-programmers/standard-features/error-reporting/enhanced-error-codes#enhanced-error-codes-representation-structure
 */
export function isRestError(object: unknown): object is RestError {
  return isErrorInformation(object)
    && isRestErrorCode(object.code)
    && isRestErrorAction(object.action)
    && isRestErrorStatus(object.status)
}

/**
 * Check if the object is a DCR error response body from Adobe.
 * @param object The error response data to check
 * @returns `true` if `object` is of type {@link DcrError}
 * @see {@link DcrErrorValues}
 */
export function isDcrError(object: unknown): object is DcrError {
  return DcrErrorValues.includes((object as DcrError)?.error)
}

/**
 * Check if the object is a DCR registration specific error response body from Adobe.
 * @param object The error response data to check
 * @returns `true` if `object` is of type {@link ClientRegistrationError}
 * @see {@link ClientRegistrationErrorValues}
 */
export function isDcrRegistrationError(object: unknown, url = ''): object is ClientRegistrationError {
  // There is overlap with the expected error code names between DCR
  // registration service (see ClientRegistrationErrorValues) and DCR token
  // service (see AccessTokenErrorValues). Here we are using the request url to
  // better narrow it down
  if (!url.includes('/o/client/register')) {
    return false
  }

  return ClientRegistrationErrorValues.includes((object as ClientRegistrationError)?.error)
}

/**
 * Check if the object is a DCR token access specific error response body from Adobe.
 * @param object The error response data to check
 * @returns `true` if `object` is of type {@link AccessTokenError}
 * @see {@link AccessTokenErrorValues}
 */
export function isDcrAccessTokenError(object: unknown, url = ''): object is AccessTokenError {
  // There is overlap with the expected error code names between DCR
  // registration service (see ClientRegistrationErrorValues) and DCR token
  // service (see AccessTokenErrorValues). Here we are using the request url to
  // better narrow it down
  if (!url.includes('/o/client/token')) {
    return false
  }

  return AccessTokenErrorValues.includes((object as AccessTokenError)?.error)
}

